import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import vw from "../../api/virtualWorld.api";
import {
  markSiblingOffersSeen,
  markTransferOfferSeen,
  offerToastId,
} from "../../hooks/useTransferOfferToasts";
import PlayerCard from "../../components/VirtualWorld/PlayerCard";
import SponsorMark from "../../components/VirtualWorld/SponsorMark";
import Loading from "../../components/Loading";

const cr = (n) => `${Number(n || 0).toLocaleString("de-DE")} €`;

const overallOf = (p) => {
  const vals = [p.scoring, p.finishing, p.nerves].map((v) => Number(v) || 0);
  const n = vals.filter((v) => v > 0);
  if (!n.length) return 0;
  return Math.round(n.reduce((s, v) => s + v, 0) / n.length);
};

const priceOf = (p) => {
  const ask = Number(p.askingPrice) || 0;
  if (ask > 0) return ask;
  return Number(p.marketValue) || 0;
};

const SORT_OPTIONS = [
  { value: "default", label: "Standard (gelistet zuerst)" },
  { value: "strength_desc", label: "Spielstärke ↓" },
  { value: "strength_asc", label: "Spielstärke ↑" },
  { value: "price_desc", label: "Preis ↓" },
  { value: "price_asc", label: "Preis ↑" },
];

const defaultDraft = (player, userId) => {
  const mine = player.isReal && String(player.userId) === String(userId);
  const wageFloor = mine ? player.selfDealMinWage || 150 : 150;
  const feeFloor = mine
    ? player.selfDealMinFee || Math.round((Number(player.marketValue) || 0) * 0.7)
    : player.askingPrice || player.marketValue || 8000;
  return {
    fee: feeFloor,
    wage: Math.max(player.suggestedWage || 450, wageFloor),
    duration: 1,
    role: "rotation",
    kind: player.listKind === "loan" ? "loan" : "transfer",
    offeredPlayerKey: "",
  };
};

const Field = ({ label, children }) => (
  <label className="text-xs block">
    <span className="block opacity-70 mb-1">{label}</span>
    {children}
  </label>
);

const inputClass = "block w-full p-2 rounded text-black";

const ManagerTransfers = () => {
  const { userId, manager, loading, refresh } = useVirtualWorld();
  const [market, setMarket] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [kindFilter, setKindFilter] = useState("");
  const [marketFilter, setMarketFilter] = useState("available");
  const [sortBy, setSortBy] = useState("strength_desc");
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [listDrafts, setListDrafts] = useState({});
  const [openOfferKey, setOpenOfferKey] = useState("");
  const [sendingKey, setSendingKey] = useState("");
  const banner = useRef(null);

  const load = () => {
    if (!userId) return;
    vw.market(userId, { filter: marketFilter || "available", kind: kindFilter }).then((res) =>
      setMarket(res.data?.players || [])
    );
    vw.clubOffers(userId)
      .then((res) => {
        const data = res.data || {};
        if (Array.isArray(data)) {
          setOutgoing(data);
          setIncoming([]);
          return;
        }
        setOutgoing(data.outgoing || []);
        setIncoming(data.incoming || []);
      })
      .catch(() => {
        setOutgoing([]);
        setIncoming([]);
      });
  };

  useEffect(load, [userId, kindFilter, marketFilter]);

  const sortedMarket = useMemo(() => {
    const list = [...(market || [])];
    const listedRank = (c) => (c.listed ? 0 : !c.clubId ? 1 : 2);
    if (sortBy === "strength_desc") {
      list.sort((a, b) => overallOf(b) - overallOf(a) || priceOf(b) - priceOf(a));
    } else if (sortBy === "strength_asc") {
      list.sort((a, b) => overallOf(a) - overallOf(b) || priceOf(a) - priceOf(b));
    } else if (sortBy === "price_desc") {
      list.sort((a, b) => priceOf(b) - priceOf(a) || overallOf(b) - overallOf(a));
    } else if (sortBy === "price_asc") {
      list.sort((a, b) => priceOf(a) - priceOf(b) || overallOf(b) - overallOf(a));
    } else {
      list.sort(
        (a, b) =>
          listedRank(a) - listedRank(b) || overallOf(b) - overallOf(a) || (b.scoring || 0) - (a.scoring || 0)
      );
    }
    return list;
  }, [market, sortBy]);

  const flash = (success, text, toastId) => {
    setOk(success);
    setMsg(text);
    if (text) {
      const opts = { autoClose: success ? 7000 : 9000 };
      if (toastId) {
        if (toast.isActive(toastId)) {
          requestAnimationFrame(() => banner.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
          return;
        }
        opts.toastId = toastId;
      }
      if (success) toast.success(text, opts);
      else toast.warning(text, opts);
    }
    requestAnimationFrame(() => banner.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const draftFor = (player) => drafts[player.playerKey] || defaultDraft(player, userId);
  const setDraft = (playerKey, patch) => {
    setDrafts((prev) => {
      const base = prev[playerKey] || defaultDraft(market.find((p) => p.playerKey === playerKey) || { playerKey }, userId);
      return { ...prev, [playerKey]: { ...base, ...patch } };
    });
  };

  const openOffer = (player) => {
    setDrafts((prev) => ({
      ...prev,
      [player.playerKey]: prev[player.playerKey] || defaultDraft(player, userId),
    }));
    setOpenOfferKey(player.playerKey);
    setMsg("");
  };

  const bid = async (player) => {
    const offer = draftFor(player);
    setMsg("");
    setSendingKey(player.playerKey);
    try {
      const res = await vw.createOffer(userId, {
        playerKey: player.playerKey,
        fee: Number(offer.fee) || 0,
        wage: Number(offer.wage) || 0,
        duration: Number(offer.duration) || 1,
        role: offer.role,
        kind: offer.kind,
        offeredPlayerKey: offer.offeredPlayerKey || undefined,
      });
      {
        const accepted = Boolean(res.data?.accepted);
        const note = res.data?.reason || res.data?.message || "Angebot gesendet.";
        const offer = res.data?.offer;
        const status = accepted ? "accepted" : offer?.status || "open";
        const tid = offerToastId(offer, status);
        if (offer && status) markTransferOfferSeen(offer, status);
        // Sofort-Ablehnung (Preis zu niedrig etc.) = Warnung, Annahme = Erfolg, pending = Info
        if (accepted) {
          flash(true, note, tid);
          try {
            const offersRes = await vw.clubOffers(userId);
            markSiblingOffersSeen(offersRes.data?.outgoing || [], player.playerKey, "accepted");
          } catch {
            /* ignore */
          }
        } else if (status === "rejected") {
          // Wenn parallel schon ein Kauf durchging: Reject-Toast unterdrücken
          try {
            const offersRes = await vw.clubOffers(userId);
            const outgoing = offersRes.data?.outgoing || [];
            const won = outgoing.some(
              (o) =>
                String(o.targetPlayerKey) === String(player.playerKey) && o.status === "accepted"
            );
            if (won) {
              markSiblingOffersSeen(outgoing, player.playerKey, "accepted");
            } else {
              flash(false, note, tid);
            }
          } catch {
            flash(false, note, tid);
          }
        } else {
          setOk(true);
          setMsg(note);
          if (!tid || !toast.isActive(tid)) {
            toast.info(note, { autoClose: 7000, ...(tid ? { toastId: tid } : {}) });
          }
        }
      }
      setOpenOfferKey("");
      await refresh();
      load();
    } catch (err) {
      flash(false, err?.data?.message || err?.message || "Transfer fehlgeschlagen.");
    } finally {
      setSendingKey("");
    }
  };

  const list = async (player, listed, listKindOverride) => {
    const draft = listDrafts[player.playerKey] || {
      askingPrice: player.askingPrice || player.marketValue || 500,
      listKind: player.listKind === "loan" ? "loan" : "sale",
    };
    const payload = listed
      ? {
          ...draft,
          listKind: listKindOverride || draft.listKind || "sale",
          askingPrice:
            listKindOverride === "loan"
              ? Number(draft.askingPrice) || 0
              : Number(draft.askingPrice) || player.marketValue || 500,
        }
      : {};
    setMsg("");
    try {
      const res = await vw.listPlayer(userId, player.playerKey, listed, payload);
      flash(true, res.data?.message || (listed ? "Auf dem Markt." : "Von der Liste genommen."));
      await refresh();
      load();
    } catch (err) {
      flash(false, err?.data?.message || err?.message || "Liste fehlgeschlagen.");
    }
  };

  const answer = async (offerId, accept) => {
    setMsg("");
    try {
      const res = await vw.respondClubOffer(userId, offerId, accept);
      flash(Boolean(res.data?.accepted) || !accept, res.data?.message || (accept ? "Angenommen." : "Abgelehnt."));
      await refresh();
      load();
    } catch (err) {
      flash(false, err?.data?.message || err?.message || "Antwort fehlgeschlagen.");
    }
  };

  if (loading && !manager) return <Loading />;
  if (!manager?.club) return <p>Bitte zuerst einen Club gründen.</p>;

  const squad = manager.players || [];
  const tradable = squad.filter((p) => !p.loanedOut && !p.loanedIn);
  const playable = squad.filter((p) => !p.loanedOut);
  const listedSale = playable.filter((p) => p.listed && p.listKind !== "loan");
  const effectiveSlots = playable.length - listedSale.length;
  const squadFull = playable.length >= 6;
  const canBuyDespiteFull = squadFull && listedSale.length > 0;
  const fin = manager.dashboard?.finance || {};
  const budget = Number(fin.budget ?? manager.club.budget ?? 0);
  const wages = Number(fin.wageBill ?? manager.club.wageBill ?? 0);
  const inflow = Number(fin.inflowPerMatchday ?? (2000 + Number(fin.sponsorPerMatchday || 0)));
  const outflow = Number(fin.outflowPerMatchday ?? wages);
  const net = Number(fin.netPerMatchday ?? inflow - outflow);
  const runway = Number(fin.runwayMatchdays ?? (wages ? Math.floor(Math.max(0, budget) / wages) : 99));
  const pressure = fin.pressure || "stabil";
  const ledger = manager.dashboard?.ledger || (manager.finances || []).slice(0, 4);
  const statusLabel = {
    open: "offen",
    accepted: "angenommen",
    rejected: "abgelehnt",
    expired: "abgelaufen",
    withdrawn: "zurückgezogen",
  };
  const kindLabel = { transfer: "Kauf", loan: "Leihe", swap: "Tausch" };
  const pressureClass =
    pressure === "hoch"
      ? "border-red-400 bg-red-50 dark:bg-red-950/30"
      : pressure === "mittel"
        ? "border-amber-400 bg-amber-50 dark:bg-amber-950/30"
        : "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/20";

  return (
    <div className="space-y-6">
      <div ref={banner} />
      {msg ? (
        <p className={`p-3 rounded font-semibold ${ok ? "bg-emerald-100 text-emerald-900" : "bg-red-100 text-red-800"}`}>
          {msg}
        </p>
      ) : null}

      <section className={`rounded-2xl border p-4 ${pressureClass}`}>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-70">Club-Finanzen · Transferentscheidung</div>
            <h2 className="text-2xl font-black flex items-center gap-2 flex-wrap">
              {fin.sponsorBrand ? <SponsorMark brand={fin.sponsorBrand} size={32} /> : null}
              Kontostand {cr(budget)}
            </h2>
          </div>
          <Link to="/manager/club" className="text-sm font-semibold underline opacity-80">
            Zur Geschäftsstelle
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div className="rounded-xl bg-white/70 dark:bg-black/20 p-3">
            <div className="text-xs opacity-60">Zufluss / Spieltag</div>
            <div className="font-black text-lg text-emerald-800 dark:text-emerald-300">+{cr(inflow)}</div>
            <div className="text-xs opacity-70 mt-1">
              Liga {cr(fin.leaguePerMatchday || 2000)}
              {fin.sponsorPerMatchday
                ? ` · ${fin.sponsorName || "Sponsor"} ${cr(fin.sponsorPerMatchday)}`
                : " · kein Sponsor"}
            </div>
            {!fin.sponsorChosen ? (
              <Link to="/manager/club" className="inline-block mt-1 text-xs font-semibold underline">
                Sponsor wählen
              </Link>
            ) : null}
          </div>
          <div className="rounded-xl bg-white/70 dark:bg-black/20 p-3">
            <div className="text-xs opacity-60">Abfluss / Spieltag</div>
            <div className="font-black text-lg text-red-800 dark:text-red-300">−{cr(outflow)}</div>
            <div className="text-xs opacity-70 mt-1">Gehälter (Wage Bill)</div>
          </div>
          <div className="rounded-xl bg-white/70 dark:bg-black/20 p-3">
            <div className="text-xs opacity-60">Saldo / Spieltag</div>
            <div className={`font-black text-lg ${net >= 0 ? "text-emerald-800 dark:text-emerald-300" : "text-red-800 dark:text-red-300"}`}>
              {net >= 0 ? "+" : ""}
              {cr(net)}
            </div>
            <div className="text-xs opacity-70 mt-1">ohne Ablösen / Heimzuschauer</div>
          </div>
          <div className="rounded-xl bg-white/70 dark:bg-black/20 p-3">
            <div className="text-xs opacity-60">Spielraum</div>
            <div className="font-black text-lg">{runway} Spieltage</div>
            <div className="text-xs opacity-70 mt-1">Druck: {pressure}</div>
          </div>
        </div>
        {ledger?.length ? (
          <ul className="mt-3 text-xs space-y-1 opacity-80">
            {ledger.slice(0, 4).map((f, i) => (
              <li key={f._id || f.id || `${f.note}-${i}`}>
                <span className={Number(f.amount) >= 0 ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300"}>
                  {Number(f.amount) >= 0 ? "+" : ""}
                  {cr(f.amount)}
                </span>
                {" · "}
                {f.note || f.type || "Buchung"}
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <p className="text-sm opacity-80">
        Links: Spieler <strong>zum Verkauf listen</strong> (Slot zählt dann als frei für Käufe), <strong>kündigen</strong>{" "}
        (NPCs bei vollem Kader ohne Abfindung) oder <strong>verleihen</strong>.
        Rechts: <strong>Kaufen</strong>, <strong>Leihen</strong> oder <strong>Tausch</strong>.
      </p>
      {squadFull ? (
        <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
          Kader {playable.length}/6
          {canBuyDespiteFull
            ? ` · ${listedSale.length} zum Verkauf gelistet → Kauf möglich (${effectiveSlots}/6 effektiv belegt). Beim Kauf wird der gelistete Spieler automatisch freigegeben.`
            : " · Zum Verkauf listen (dann Kauf möglich), unter Mannschaft NPCs kündigen, verleihen oder tauschen."}
        </p>
      ) : null}

      {incoming.length || outgoing.length ? (
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          {incoming.length ? (
            <div>
              <h3 className="font-bold mb-2">Eingehende Gebote</h3>
              <ul className="space-y-2">
                {incoming.map((o) => (
                  <li key={o._id || o.id} className="bg-white dark:bg-gray-700 p-3 rounded-lg flex flex-wrap gap-3 items-center justify-between">
                    <div>
                      <div className="font-semibold">
                        {kindLabel[o.kind] || o.kind}: {o.targetName || o.targetPlayerKey}
                        {o.offeredName ? ` gegen ${o.offeredName}` : ""}
                      </div>
                      <div className="text-xs opacity-70 mt-0.5">
                        von {o.offeringClubName || "einem Club"}
                      </div>
                      <div className="opacity-80">
                        {cr(o.fee)} {o.kind === "loan" ? "Leihgebühr" : o.kind === "swap" ? "Ausgleich" : "Ablöse"}
                        {o.kind !== "loan" ? ` · Gehalt ${cr(o.wage)}` : ""}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="px-3 py-1 rounded bg-green-500 text-white" onClick={() => answer(o._id || o.id, true)}>Annehmen</button>
                      <button className="px-3 py-1 rounded bg-gray-200" onClick={() => answer(o._id || o.id, false)}>Ablehnen</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : <div />}
          {outgoing.length ? (
            <div>
              <h3 className="font-bold mb-2">Gesendete Angebote</h3>
              <ul className="space-y-1 opacity-90">
                {outgoing.slice(0, 8).map((o) => (
                  <li key={o._id || o.id} className="mb-2">
                    <div>
                      {kindLabel[o.kind] || o.kind} · {o.targetName || o.targetPlayerKey} ·{" "}
                      {statusLabel[o.status] || o.status} · {cr(o.fee)}
                    </div>
                    {o.decisionNote || o.reason ? (
                      <div className="text-xs opacity-70 mt-0.5">{o.decisionNote || o.reason}</div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <section className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-600 p-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
          <h2 className="text-2xl font-bold mb-1">Kader</h2>
          <p className="text-sm opacity-70 mb-4">
            Verkauf oder Leihe anbieten. Sofort freien Slot: Mannschaft → Kündigen.
          </p>
          <div className="space-y-4">
            {tradable.map((p) => {
              const draft = listDrafts[p.playerKey] || {
                askingPrice: p.askingPrice || p.marketValue || 500,
                listKind: p.listKind === "loan" ? "loan" : "sale",
              };
              return (
                <PlayerCard
                  key={p.playerKey}
                  player={p}
                  compact
                  actions={
                    p.listed ? (
                      <button type="button" className="px-3 py-1 rounded bg-gray-200" onClick={() => list(p, false)}>
                        Von der Liste nehmen
                      </button>
                    ) : (
                      <div className="w-full space-y-2">
                        <Field label={draft.listKind === "loan" ? "Leihgebühr" : "Verkaufsforderung"}>
                          <input
                            className={inputClass}
                            type="number"
                            min={draft.listKind === "loan" ? 0 : 500}
                            value={draft.askingPrice}
                            onChange={(e) =>
                              setListDrafts((prev) => ({
                                ...prev,
                                [p.playerKey]: { ...draft, askingPrice: Number(e.target.value) },
                              }))
                            }
                          />
                        </Field>
                        <p className="text-xs opacity-70">Marktwert {cr(p.marketValue)}.</p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="px-3 py-1 rounded bg-gray-900 text-white"
                            onClick={() => {
                              setListDrafts((prev) => ({
                                ...prev,
                                [p.playerKey]: { ...draft, listKind: "sale" },
                              }));
                              list(p, true, "sale");
                            }}
                          >
                            Verkaufen
                          </button>
                          <button
                            type="button"
                            className="px-3 py-1 rounded bg-amber-700 text-amber-50"
                            onClick={() => {
                              setListDrafts((prev) => ({
                                ...prev,
                                [p.playerKey]: { ...draft, listKind: "loan", askingPrice: Number(draft.askingPrice) || 0 },
                              }));
                              list(p, true, "loan");
                            }}
                          >
                            Verleihen
                          </button>
                        </div>
                      </div>
                    )
                  }
                />
              );
            })}
          </div>
        </section>

        <section className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-600 p-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <div className="mr-auto">
              <h2 className="text-2xl font-bold">Transfermarkt</h2>
              <p className="text-sm opacity-70">Erst Angebot öffnen, dann Betrag setzen.</p>
            </div>
            <select
              className="p-2 rounded text-black"
              value={marketFilter}
              onChange={(e) => setMarketFilter(e.target.value)}
              title="Marktfilter"
            >
              <option value="available">Alle handelbar</option>
              <option value="trade">Leihe / Tausch (mit Club)</option>
              <option value="listed">Nur gelistet</option>
              <option value="free">Nur Free Agents</option>
            </select>
            <select className="p-2 rounded text-black" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
              <option value="">Alle Typen</option>
              <option value="real">Echte Avatare</option>
              <option value="flowliga">FlowLiga-Spiegel</option>
              <option value="npc">NPC</option>
            </select>
            <select
              className="p-2 rounded text-black"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              title="Sortierung"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-4">
            {sortedMarket.length ? sortedMarket.map((p) => {
              const mine = p.isReal && String(p.userId) === String(userId);
              const offer = draftFor(p);
              const alreadyMine = p.clubId && String(p.clubId) === String(manager.club.id);
              return (
                <PlayerCard
                  key={p.playerKey}
                  player={p}
                  compact
                  actions={
                    alreadyMine && !p.loanedOut ? (
                      <p className="text-xs opacity-80">Dein Spieler — links verkaufen oder verleihen.</p>
                    ) : openOfferKey !== p.playerKey ? (
                      <div className="w-full space-y-2">
                        {p.clubId && !p.listed ? (
                          <p className="text-xs opacity-80">Bei einem Club — Leihe oder Tausch möglich (Angebot öffnen).</p>
                        ) : null}
                        {p.listed && p.askingPrice != null ? (
                          <p className="text-xs opacity-80">
                            Forderung {cr(p.askingPrice)}
                            {p.listKind === "loan" ? " (Leihe)" : ""}.
                          </p>
                        ) : null}
                        {mine ? (
                          <p className="text-xs opacity-80">Mindest-Ablöse {cr(p.selfDealMinFee || Math.round((p.marketValue || 0) * 0.7))}.</p>
                        ) : null}
                        <button type="button" className="px-3 py-1 rounded bg-green-500 text-white border border-yellow-400" onClick={() => openOffer(p)}>
                          {mine ? "Mir selbst anbieten" : p.clubId ? "Kaufen / Leihen / Tausch" : "Angebot machen"}
                        </button>
                      </div>
                    ) : (
                      <div className="w-full space-y-2">
                        {mine ? (
                          <p className="text-xs opacity-80">
                            Eigenes Angebot: danach unter <Link className="underline" to="/career/offers">Karriere → Angebote</Link> annehmen.
                            Mindest-Ablöse {cr(p.selfDealMinFee || Math.round((p.marketValue || 0) * 0.7))} · Mindestgehalt {cr(p.selfDealMinWage)}.
                          </p>
                        ) : p.listed && p.askingPrice ? (
                          <p className="text-xs opacity-80">
                            Forderung {cr(p.askingPrice)}
                            {p.listKind === "loan" ? " (Leihe)" : ""}. Gebot auf oder über der Forderung wird sofort ausgeführt.
                          </p>
                        ) : null}
                        <div className="grid grid-cols-2 gap-2">
                          <Field label="Art">
                            <select className={inputClass} value={offer.kind} onChange={(e) => setDraft(p.playerKey, { kind: e.target.value })}>
                              <option value="transfer">{mine ? "Selbst verpflichten" : "Kaufen"}</option>
                              {!mine && p.clubId ? <option value="loan">Leihen</option> : null}
                              {!mine && p.clubId && tradable.length ? <option value="swap">Tausch</option> : null}
                            </select>
                          </Field>
                          {!mine && !p.clubId ? (
                            <p className="col-span-2 text-xs opacity-70">
                              Free Agent — nur Kauf. Leihe und Tausch gibt es nur bei Spielern, die noch bei einem Club sind
                              (Filter „Leihe / Tausch“).
                            </p>
                          ) : null}
                          <Field label={offer.kind === "loan" ? "Leihgebühr" : offer.kind === "swap" ? "Ausgleich" : "Ablöse"}>
                            <input
                              className={inputClass}
                              type="number"
                              min={offer.kind === "loan" || offer.kind === "swap" ? 0 : mine ? (p.selfDealMinFee || 500) : 500}
                              value={offer.fee}
                              onChange={(e) => setDraft(p.playerKey, { fee: Number(e.target.value) })}
                            />
                          </Field>
                          {offer.kind !== "loan" ? (
                            <Field label="Gehalt">
                              <input
                                className={inputClass}
                                type="number"
                                min={mine ? p.selfDealMinWage : 150}
                                value={offer.wage}
                                onChange={(e) => setDraft(p.playerKey, { wage: Number(e.target.value) })}
                              />
                            </Field>
                          ) : null}
                          <Field label="Saisons">
                            <input
                              className={inputClass}
                              type="number"
                              min="1"
                              max="3"
                              value={offer.duration}
                              onChange={(e) => setDraft(p.playerKey, { duration: Number(e.target.value) })}
                            />
                          </Field>
                          {offer.kind === "swap" ? (
                            <Field label="Dein Spieler">
                              <select className={inputClass} value={offer.offeredPlayerKey} onChange={(e) => setDraft(p.playerKey, { offeredPlayerKey: e.target.value })}>
                                <option value="">wählen</option>
                                {tradable.map((own) => (
                                  <option key={own.playerKey} value={own.playerKey}>
                                    {own.name} ({cr(own.marketValue)})
                                  </option>
                                ))}
                              </select>
                            </Field>
                          ) : (
                            <Field label="Rolle">
                              <select className={inputClass} value={offer.role} onChange={(e) => setDraft(p.playerKey, { role: e.target.value })}>
                                <option value="star">Star</option>
                                <option value="starter">Starter</option>
                                <option value="rotation">Rotation</option>
                                <option value="talent">Talent</option>
                              </select>
                            </Field>
                          )}
                        </div>
                        <p className="text-xs opacity-80">
                          {offer.kind === "loan"
                            ? `Leihgebühr ${cr(offer.fee)} für ${offer.duration} Saison(s).`
                            : offer.kind === "swap"
                              ? `Tausch${offer.offeredPlayerKey ? "" : " (Spieler wählen)"} · Ausgleich ${cr(offer.fee)}.`
                              : `Ablöse ${cr(offer.fee)} · Gehalt ${cr(offer.wage)} · ${offer.duration} Saison(s).`}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={sendingKey === p.playerKey || (offer.kind === "swap" && !offer.offeredPlayerKey)}
                            className="px-3 py-1 rounded bg-green-500 text-white border border-yellow-400 disabled:opacity-50"
                            onClick={() => bid(p)}
                          >
                            {sendingKey === p.playerKey ? "Sende …" : "Angebot senden"}
                          </button>
                          <button type="button" className="px-3 py-1 rounded bg-gray-200" onClick={() => setOpenOfferKey("")}>
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    )
                  }
                />
              );
            }) : (
              <p className="text-sm opacity-70">Niemand auf dem Markt. Spieler links listen, dann tauchen sie hier auf.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ManagerTransfers;
